/**
 * Safe URL utilities.
 *
 * Guards every browser navigation / window.open against script-bearing URLs
 * (`javascript:`, `data:`, `vbscript:`, ...). Only same-origin relative paths and
 * absolute `http(s)` URLs are considered safe destinations.
 *
 * Security: HCMI-831 - Potential Cross Site Scripting (XSS) via window.location.href.
 */

/** Protocols allowed for absolute URLs. */
const ALLOWED_PROTOCOLS: readonly string[] = ['http:', 'https:'];

/**
 * Removes characters browsers strip before resolving a URL scheme
 * (control chars and whitespace), so `java\nscript:` cannot bypass the check.
 */
function stripSchemeObfuscation(url: string): string {
  // eslint-disable-next-line no-control-regex
  return url.replace(/[\u0000-\u0020]/g, '');
}

/**
 * Checks whether a URL is safe to navigate to.
 *
 * Accepts:
 * - Relative paths (`/login`, `dashboard`, `?a=1`, `#section`)
 * - Absolute `http:` / `https:` URLs
 *
 * Rejects everything else, including `javascript:`, `data:`, `blob:` and
 * protocol-relative URLs (`//evil.com`).
 *
 * @param url - Candidate URL
 * @returns `true` when the URL is safe to use in a redirect or `window.open`
 *
 * @example
 * isSafeUrl('/login');                    // true
 * isSafeUrl('https://example.com/a.pdf'); // true
 * isSafeUrl('javascript:alert(1)');       // false
 * isSafeUrl('//evil.com');                // false
 */
export function isSafeUrl(url: string): boolean {
  if (typeof url !== 'string') {
    return false;
  }

  // Browsers normalize `\` to `/` for special schemes, so `\\evil.com` and `/\evil.com`
  // both resolve off-origin. Normalize before the checks or they slip past the `//` guard.
  const candidate = stripSchemeObfuscation(url).replace(/\\/g, '/');

  if (candidate.length === 0) {
    return false;
  }

  // Protocol-relative URLs (`//host`) inherit the current scheme and can point anywhere.
  if (candidate.startsWith('//')) {
    return false;
  }

  // Relative paths, query strings and fragments never carry a scheme.
  if (candidate.startsWith('/') || candidate.startsWith('?') || candidate.startsWith('#')) {
    return true;
  }

  try {
    // A base is required so scheme-less relative URLs (`dashboard`) resolve instead of throwing.
    const parsed = new URL(candidate, window.location.origin);
    return ALLOWED_PROTOCOLS.includes(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * Returns the URL when it is safe, or `null` when it must not be used.
 *
 * @param url - Candidate URL
 * @returns The original URL, or `null` when unsafe
 *
 * @example
 * sanitizeUrl('/login');              // '/login'
 * sanitizeUrl('javascript:alert(1)'); // null
 */
export function sanitizeUrl(url: string): string | null {
  return isSafeUrl(url) ? url : null;
}
